import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { authedFetch } from '../lib/api';
import { API, apiPath } from '../lib/constants';
import { logger } from '../lib/logger';
import { usePolling } from '../hooks/usePolling';
import ChatSidebar from './ChatSidebar';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import type { Agent, Model, Message, ChatSession } from '@/types/dashboard';

// Default thinking message when no real status is available yet
const DEFAULT_THINKING = 'Thinking...';

export default function AgentChat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activityStatus, setActivityStatus] = useState(DEFAULT_THINKING);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(true);
  const [totalSessionCount, setTotalSessionCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const agentsReadyRef = useRef<Promise<string> | null>(null);
  const modelsReadyRef = useRef<Promise<string> | null>(null);

  // Polling state
  const [pollingSessionId, setPollingSessionId] = useState<string | null>(null);
  const lastPollHashRef = useRef<string>('');

  // Track elapsed time while loading
  useEffect(() => {
    if (!isLoading) {
      setActivityStatus(DEFAULT_THINKING);
      setElapsedTime(0);
      return;
    }
    const loadingStartTime = Date.now();
    const timeInterval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - loadingStartTime) / 1000));
    }, 1000);
    return () => clearInterval(timeInterval);
  }, [isLoading]);

  useEffect(() => {
    agentsReadyRef.current = loadAgents();
    modelsReadyRef.current = loadModels();
    if (user) loadChatSessions();
  }, [user]);

  // Polling via usePolling hook
  const fetchSession = useCallback(async () => {
    if (!pollingSessionId) throw new Error('No session to poll');
    const response = await authedFetch(apiPath.chatSession(pollingSessionId));
    return response.json();
  }, [pollingSessionId]);

  const handlePollData = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => {
      if (!data.session) return;
      const loadedMessages = data.session.messages || [];
      const pollHash = loadedMessages
        .map((m: Message) => `${m.id}:${m.content?.length}:${m.pending}`)
        .join('|');
      const messagesChanged = pollHash !== lastPollHashRef.current;
      if (messagesChanged) lastPollHashRef.current = pollHash;

      const stillPending =
        loadedMessages.some((m: Message) => m.pending) || data.session.has_pending;

      if (!stillPending) {
        if (messagesChanged) setMessages(loadedMessages);
        setSessionId(data.session.adk_session_id || null);
        setIsLoading(false);
        setPollingSessionId(null);
        loadChatSessions();
        return;
      }

      if (messagesChanged) setMessages(loadedMessages);
      if (data.session.status) setActivityStatus(data.session.status);
    },
    [pollingSessionId],
  );

  const handlePollError = useCallback(
    (err: unknown) => {
      logger.error('Poll error:', err);
      setIsLoading(false);
      setPollingSessionId(null);
    },
    [],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isPollComplete = useCallback((data: any) => {
    if (!data.session) return true;
    const loadedMessages = data.session.messages || [];
    return !(loadedMessages.some((m: Message) => m.pending) || data.session.has_pending);
  }, []);

  usePolling({
    fetchFn: fetchSession,
    isComplete: isPollComplete,
    onData: handlePollData,
    onError: handlePollError,
    intervalMs: 1000,
    maxAttempts: 3600,
    enabled: !!pollingSessionId,
  });

  const loadChatSessions = async () => {
    if (!user) return;
    try {
      const response = await authedFetch(API.CHAT_SESSIONS);
      const data = await response.json();
      setChatSessions(data.sessions || []);
      setTotalSessionCount(data.total_count ?? (data.sessions || []).length);
    } catch (err) {
      logger.error('Failed to load chat sessions:', err);
    }
  };

  const loadMoreSessions = async () => {
    if (!user || loadingMore || chatSessions.length === 0) return;
    const lastId = chatSessions[chatSessions.length - 1].id;
    setLoadingMore(true);
    try {
      const response = await authedFetch(apiPath.chatSessionsAfter(lastId));
      const data = await response.json();
      const more = data.sessions || [];
      if (more.length > 0) setChatSessions(prev => [...prev, ...more]);
      setTotalSessionCount(data.total_count ?? totalSessionCount);
    } catch (err) {
      logger.error('Failed to load more sessions:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadSession = async (sessionIdToLoad: string) => {
    if (!user) return;
    setPollingSessionId(null);
    setSidebarOpen(false);
    try {
      const response = await authedFetch(apiPath.chatSession(sessionIdToLoad));
      const data = await response.json();
      if (data.session) {
        setCurrentSessionId(sessionIdToLoad);
        setSessionId(data.session.adk_session_id || null);
        const loadedMessages = data.session.messages || [];
        setMessages(loadedMessages);
        setSelectedAgent(data.session.agent_path);
        setSelectedModel(data.session.model);

        const hasPendingMessage = loadedMessages.some((m: Message) => m.pending);
        if (hasPendingMessage || data.session.has_pending) {
          setIsLoading(true);
          setPollingSessionId(sessionIdToLoad);
        } else {
          setIsLoading(false);
        }
      }
    } catch (err) {
      logger.error('Failed to load session:', err);
    }
  };

  const deleteSession = async (sid: string) => {
    if (!user) return;
    try {
      await authedFetch(apiPath.chatSession(sid), { method: 'DELETE' });
      if (currentSessionId === sid) startNewChat();
      loadChatSessions();
    } catch (err) {
      logger.error('Failed to delete session:', err);
    }
  };

  const startNewChat = () => {
    setPollingSessionId(null);
    setMessages([]);
    setSessionId(null);
    setCurrentSessionId(null);
    setIsLoading(false);
  };

  const loadAgents = async (): Promise<string> => {
    try {
      const response = await authedFetch(API.CHAT_AGENTS);
      const data = await response.json();
      const chatAgents = (data.agents || []).filter((agent: Agent) => {
        const nameLower = agent.name.toLowerCase();
        const pathLower = agent.path.toLowerCase();
        const displayLower = agent.display_name.toLowerCase();
        return !nameLower.includes('eval') && !pathLower.includes('eval') && !displayLower.includes('eval');
      });
      if (chatAgents.length > 0 && !selectedAgent) {
        const agentPath = chatAgents[chatAgents.length - 1].path;
        setSelectedAgent(agentPath);
        return agentPath;
      }
    } catch (err) {
      logger.error('Failed to load agents:', err);
    }
    return '';
  };

  const loadModels = async (): Promise<string> => {
    try {
      const response = await authedFetch(API.CHAT_MODELS);
      const data = await response.json();
      if (data.models && data.models.length > 0 && !selectedModel) {
        const modelList = data.models as Model[];
        const gemini3Pro = modelList.find((m) => m.name.includes('gemini-3') && m.name.includes('pro'));
        const gemini3Flash = modelList.find((m) => m.name.includes('gemini-3') && m.name.includes('flash'));
        const gemini25Pro = modelList.find((m) => m.name.includes('gemini-2.5') && m.name.includes('pro'));
        const gemini25Flash = modelList.find((m) => m.name.includes('gemini-2.5') && m.name.includes('flash'));
        const bestModel = gemini3Pro || gemini3Flash || gemini25Pro || gemini25Flash || modelList[0];
        setSelectedModel(bestModel.name);
        return bestModel.name;
      }
    } catch (err) {
      logger.error('Failed to load models:', err);
    }
    return '';
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith('.csv') && !lowerName.endsWith('.xlsx')) return;
    if (file.size > 10 * 1024 * 1024) return;
    setAttachedFile(file);
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const question = inputValue.trim();
    setInputValue('');
    setIsLoading(true);

    const fileToUpload = attachedFile;
    setAttachedFile(null);

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: fileToUpload ? `\u{1F4CE} ${fileToUpload.name}\n${question}` : question,
    };
    const pendingMessage: Message = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: '',
      pending: true,
    };
    setMessages(prev => [...prev, userMessage, pendingMessage]);

    const agentPath = selectedAgent || (agentsReadyRef.current ? await agentsReadyRef.current : '');
    const modelName = selectedModel || (modelsReadyRef.current ? await modelsReadyRef.current : '');

    try {
      let uploadedFiles: { gcs_uri: string; filename: string }[] = [];
      if (fileToUpload) {
        setActivityStatus('Uploading file...');
        const formData = new FormData();
        formData.append('file', fileToUpload);
        const uploadResponse = await authedFetch(API.CHAT_UPLOAD, {
          method: 'POST',
          body: formData,
        });
        if (!uploadResponse.ok) throw new Error('Failed to upload file');
        const uploadResult = await uploadResponse.json();
        uploadedFiles = [{ gcs_uri: uploadResult.gcs_uri, filename: uploadResult.filename }];
      }

      const response = await authedFetch(API.CHAT_SEND, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: currentSessionId,
          question,
          agent_path: agentPath,
          model: modelName,
          adk_session_id: sessionId,
          uploaded_files: uploadedFiles.length > 0 ? uploadedFiles : undefined,
        }),
      });

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Failed to send message');

      if (result.session_id && !currentSessionId) setCurrentSessionId(result.session_id);

      setPollingSessionId(result.session_id);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setMessages(prev => {
        const withoutPending = prev.filter(m => m.id !== pendingMessage.id);
        return [...withoutPending, {
          id: pendingMessage.id,
          role: 'assistant' as const,
          content: `Error: ${message}`,
          error: message,
          pending: false,
        }];
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) setSessionId(null);
  }, [selectedAgent, selectedModel]);

  const canSend = !!inputValue.trim() && !isLoading && !!user;

  return (
    <div className="flex flex-col lg:flex-row h-full bg-linear-to-br from-background via-background to-primary/5">
      <ChatSidebar
        sessions={chatSessions}
        currentSessionId={currentSessionId}
        totalCount={totalSessionCount}
        showHistory={showHistory}
        sidebarOpen={sidebarOpen}
        loadingMore={loadingMore}
        onToggleHistory={() => setShowHistory(!showHistory)}
        onToggleSidebar={setSidebarOpen}
        onLoadSession={loadSession}
        onDeleteSession={deleteSession}
        onNewChat={startNewChat}
        onLoadMore={loadMoreSessions}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-h-0">
        <MessageList
          messages={messages}
          isLoading={isLoading}
          activityStatus={activityStatus}
          elapsedTime={elapsedTime}
          onSuggestionClick={setInputValue}
        />

        <ChatInput
          value={inputValue}
          onChange={setInputValue}
          onSend={sendMessage}
          canSend={canSend}
          isLoading={isLoading}
          attachedFile={attachedFile}
          onFileSelect={handleFileSelect}
          onRemoveFile={() => setAttachedFile(null)}
        />
      </div>
    </div>
  );
}
